import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { SortableLinesTable, Journal2GridView, PdfDownloadButton } from './AdminAfasTransactions';
import AfasArticlePiP from './AfasArticlePiP';
import { authHeaders } from '../utils/apiAuth';
import { errorText } from '../utils/apiErrors';
import { CAP } from '../utils/permissions';
import { usePermissions } from '../utils/PermissionContext';

const AdminAfasTransactionInnerDetails = ({
  trx,
  orderData,
  journal2ViewMode,
  setJournal2ViewMode,
  apiUrl,
  language,
  t
}) => {
  const { can } = usePermissions();
  const canWrite = can(CAP.ADMIN_WRITE);
  const [isReCreating, setIsReCreating] = useState(false);
  const [isCreatingCreditNote, setIsCreatingCreditNote] = useState(false);
  const [isCreatingPdf, setIsCreatingPdf] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAfasPiP, setShowAfasPiP] = useState(false);
  const [afasArticles, setAfasArticles] = useState([]);
  const [pdfExists, setPdfExists] = useState(null);

  const openAfasPiP = async () => {
    setShowAfasPiP(true);
    if (afasArticles.length === 0) {
      try {
        const response = await fetch(`${apiUrl}/api/afas-articles`, {
          headers: authHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setAfasArticles(data);
        }
      } catch (err) {
        console.error("Failed to fetch AFAS articles", err);
      }
    }
  };

  const lines = trx.lines || [];
  const journal1Lines = lines.filter(l => l.BookTypeCode === "40");
  const journal2Lines = lines.filter(l => l.BookTypeCode === "88");
  const otherLines = lines.filter(l => l.BookTypeCode !== "40" && l.BookTypeCode !== "88");

  const handleSubmitToAfas = async () => {
    setIsSubmitting(true);
    const loadingToast = toast.loading('Submitting order to AFAS...');
    try {
      const res = await fetch(`${apiUrl}/afas/transactions/${trx.VoucherNr}/submit`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(errorText(data, t, 'Failed to submit missing order'));
      }
      toast.success(t?.submit_success || 'Successfully submitted order to AFAS!', { id: loadingToast });
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      console.error(err);
      toast.error(err.message, { id: loadingToast });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateCreditNote = async () => {
    if (!window.confirm(t?.confirm_create_credit_note || "Are you sure you want to create a credit note for this transaction?")) {
      return;
    }
    setIsCreatingCreditNote(true);
    const loadingToast = toast.loading(t?.creating_credit_note || 'Creating credit note...');
    try {
      const res = await fetch(`${apiUrl}/api/orders/${trx.VoucherNr}/credit-note`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(errorText(data, t, 'Failed to create credit note'));
      }
      toast.success(data.message || 'Successfully created credit note!', { id: loadingToast });
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      console.error(err);
      toast.error(err.message, { id: loadingToast });
    } finally {
      setIsCreatingCreditNote(false);
    }
  };

  const handleCreatePdf = async () => {
    setIsCreatingPdf(true);
    const loadingToast = toast.loading('Creating PDF and pushing to AFAS...');
    try {
      const res = await fetch(`${apiUrl}/api/orders/${trx.VoucherNr}/generate-pdf`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(errorText(data, t, 'Failed to create PDF'));
      }
      toast.success(data.message || 'Successfully created PDF!', { id: loadingToast });
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      console.error(err);
      toast.error(err.message, { id: loadingToast });
    } finally {
      setIsCreatingPdf(false);
    }
  };

  const handleReCreate = async () => {
    // Validate if any cart item is missing the AFAS Article Code
    if (orderData && orderData.cartItems) {
      const missingItems = orderData.cartItems.filter(item => !item.afasArticleCode && !item.afas_artikelcode);
      if (missingItems.length > 0) {
        const itemNames = missingItems.map(item => item.name || item.skuName || 'Unknown Product').join(', ');
        toast.error(`AFAS Article Code is missing for: ${itemNames}. Please check it again.`);
        return; // Abort API call
      }
    }

    setIsReCreating(true);
    const loadingToast = toast.loading('Re-creating order and updating journals...');
    try {
      const res = await fetch(`${apiUrl}/afas/transactions/${trx.VoucherNr}/re-create`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(errorText(data, t, 'Failed to re-create order'));
      }
      toast.success(data.message || 'Successfully created new journals!', { id: loadingToast });
      // Redirect to the new order details page
      setTimeout(() => {
        window.location.href = `/admin/afas-transactions/${data.newOrderId}`;
      }, 1500);
    } catch (err) {
      console.error(err);
      toast.error(err.message, { id: loadingToast });
    } finally {
      setIsReCreating(false);
    }
  };

  const handleDeleteTransaction = async () => {
    if (!window.confirm(t?.confirm_delete || 'Are you sure you want to delete this transaction?')) {
      return;
    }

    const loadingToast = toast.loading('Deleting transaction...');
    try {
      const res = await fetch(`${apiUrl}/afas/transactions/${trx.VoucherNr}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      if (res.ok) {
        toast.success('Successfully deleted transaction', { id: loadingToast });
        setTimeout(() => {
          window.location.href = `/admin/afas-transactions`;
        }, 1500);
      } else {
        const errorData = await res.json();
        toast.error(`Failed to delete: ${errorData.error || 'Unknown error'}`, { id: loadingToast });
      }
    } catch (err) {
      console.error(err);
      toast.error('Error deleting transaction', { id: loadingToast });
    }
  };

  return (
    <div className="relative transition-all duration-300">
      <div className="relative">
        {isReCreating && (
          <div className="absolute inset-0 bg-white/70 backdrop-blur-[2px] z-50 rounded-lg flex items-center justify-center">
            {/* The spinner and text are optional here since there is a toast, but this matches the blur effect request */}
          </div>
        )}
        {/* AFAS Error Message */}
        {orderData && orderData.afasError && (
          <div className="mb-6 p-3 bg-red-50 border border-red-500 rounded-lg text-red-600 font-medium text-sm flex items-start gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div className="break-all whitespace-pre-wrap">
              {orderData.afasError}
            </div>
          </div>
        )}

        {/* Order Meta Grid */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-semibold text-gray-700 uppercase text-xs tracking-wider flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {t.order_information || 'Order Information'}
            </h4>
            {/* PdfDownloadButton stays for everyone — it only reads. Submit,
              re-create and delete all write, so they are gone without
              adminWrite rather than sitting there ready to 403. */}
            <div className="flex items-center gap-2 relative z-[51]">
              {canWrite && (trx.missingAfas ? (
                <button
                  onClick={handleSubmitToAfas}
                  disabled={isSubmitting}
                  className={`text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded border transition-colors flex items-center gap-1
                  ${isSubmitting ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-primary text-white border-primary hover:bg-purple-800'}`}
                  title="Submit missing order to AFAS"
                >
                  {isSubmitting ? (
                    <svg className="animate-spin h-3.5 w-3.5 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  )}
                  {t?.submit || 'Submit'}
                </button>
              ) : (
                <>
                  {!orderData?.isCreditNote && !orderData?.creditedBy && (
                    <button
                      onClick={handleCreateCreditNote}
                      disabled={isCreatingCreditNote}
                      className={`text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded border transition-colors flex items-center gap-1
                      ${isCreatingCreditNote ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-red-600 border-red-600 hover:bg-red-50 group'}`}
                      title="Create credit note"
                    >
                      {isCreatingCreditNote ? (
                        <svg className="animate-spin h-3.5 w-3.5 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 group-hover:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )}
                      {t?.create_credit_notes || 'create credit notes'}
                    </button>
                  )}
                  {pdfExists === false && (
                    <button
                      onClick={handleCreatePdf}
                      disabled={isCreatingPdf}
                      className={`text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded border transition-colors flex items-center gap-1
                      ${isCreatingPdf ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-indigo-600 border-indigo-600 hover:bg-indigo-50 group'}`}
                      title="Create PDF"
                    >
                      {isCreatingPdf ? (
                        <svg className="animate-spin h-3.5 w-3.5 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 group-hover:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      )}
                      {t?.create_pdf || 'create PDF'}
                    </button>
                  )}
                  <button
                    onClick={handleReCreate}
                    disabled={isReCreating}
                    className={`text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded border transition-colors flex items-center gap-1
                  ${isReCreating ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-primary text-white border-primary hover:bg-purple-800'}`}
                    title="Re-evaluate the order and create new AFAS journals"
                  >
                    {isReCreating ? (
                      <svg className="animate-spin h-3.5 w-3.5 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    )}
                    {t?.re_create || 're-create'}
                  </button>
                </>
              ))}
              <PdfDownloadButton voucherNr={trx.VoucherNr} apiUrl={apiUrl} t={t} onPdfChecked={setPdfExists} />
              {canWrite && <button
                onClick={handleDeleteTransaction}
                title={t?.delete || 'Delete'}
                className="ml-2 flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 rounded-md transition-colors text-xs font-bold uppercase tracking-wider shadow-sm group"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 group-hover:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>}
            </div>
          </div>
          {orderData ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: t?.status || 'Status', value: orderData.status, color: orderData.status === 'Pending' ? 'text-orange-600 bg-orange-50 border-orange-100' : (orderData.status === 'Failed' ? 'text-red-600 bg-red-50 border-red-100' : 'text-green-600 bg-green-50 border-green-100') },
              (() => {
                const hasValidCustomerId = orderData.customerId && String(orderData.customerId).trim() !== '0';
                const debtorId = orderData.debtorId || trx?.debtorId;
                const debtorName = orderData.debtorName || trx?.debtorName;
                if (hasValidCustomerId) {
                  return {
                    label: t?.customer_id || 'Customer ID',
                    value: orderData.customerId,
                    linkUrl: `/admin/customers/${orderData.customerId}`,
                    domain: orderData.customerDomain || trx?.customerDomain,
                    isDebtor: false
                  };
                }
                if (debtorId) {
                  return {
                    label: t?.afas_debtor_number || 'Debtor Number',
                    value: debtorId,
                    linkUrl: `/admin/customers/${debtorId}`,
                    domain: debtorName || orderData.customerDomain || orderData.customerName || trx?.customerDomain,
                    isDebtor: true
                  };
                }
                return {
                  label: t?.customer_id || 'Customer ID',
                  value: orderData.customerName || '-',
                  linkUrl: null,
                  domain: orderData.customerDomain || trx?.customerDomain,
                  isDebtor: false
                };
              })(),
                { label: t?.order_user || 'Order User', value: orderData.orderUser, picture: orderData.orderUserProfilePicture },
                { label: t?.payment_method || 'Payment Method', value: orderData.paymentMethod, receiptUrl: orderData.stripeReceiptUrl || orderData.receiptUrl },
                { label: t?.afas_journal_id || 'AFAS Journal ID', value: orderData.afasJournalEntryId },
                { label: t?.subtotal || 'Subtotal', value: `€ ${parseFloat(orderData.subtotal || 0).toFixed(2)}` },
                { label: t?.vat_amount || 'VAT Amount', value: `€ ${parseFloat(orderData.vatAmount || 0).toFixed(2)}` },
                { label: t?.total_price || 'Total Price', value: `€ ${parseFloat(orderData.totalPrice || 0).toFixed(2)}`, highlight: true },
              ].map((stat, idx) => (
                <div key={idx} className={`p-3 rounded-lg border bg-white shadow-sm flex flex-col justify-between ${stat.highlight ? 'border-primary/20 ring-1 ring-primary/5' : 'border-gray-200'}`}>
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">{stat.label}</span>
                  {stat.linkUrl && stat.value ? (
                    <div className="flex items-center gap-2 truncate">
                      <Link
                        to={stat.linkUrl}
                        className={`text-sm font-bold cursor-pointer transition-colors truncate shrink-0 px-1.5 py-0.5 rounded border ${
                          stat.isDebtor
                            ? 'text-purple-700 hover:text-purple-900 hover:underline bg-purple-50 hover:bg-purple-100 border-purple-200'
                            : 'text-indigo-600 hover:text-indigo-800 hover:underline bg-indigo-50 border-indigo-100'
                        }`}
                      >
                        {stat.value}
                      </Link>
                      {stat.domain && (
                        <span className="text-[11px] font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200 truncate">
                          {stat.domain}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 truncate">
                      <span className={`text-sm font-semibold truncate flex items-center gap-2 ${stat.color ? `px-2 py-0.5 rounded-md inline-flex w-fit ${stat.color.split(' ')[0]} ${stat.color.split(' ')[1]} ${stat.color.split(' ')[2]}` : (stat.highlight ? 'text-primary' : 'text-gray-800')}`}>
                        {stat.picture && (
                          <img
                            src={stat.picture}
                            alt="Avatar"
                            className="w-6 h-6 rounded-full object-cover border border-gray-200 shrink-0"
                            referrerPolicy="no-referrer"
                          />
                        )}
                        {stat.label === (t?.payment_method || 'Payment Method') && stat.value && ['paypal', 'stripe', 'card'].includes(stat.value.toLowerCase()) && (
                          <img src={`/${stat.value.toLowerCase()}-logo.png`} alt={stat.value} className="h-4 w-auto object-contain" />
                        )}
                        <span>{stat.value || (t?.not_applicable || 'N/A')}</span>
                      </span>
                      {stat.receiptUrl && (
                        <a
                          href={stat.receiptUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium text-indigo-600 hover:text-indigo-800 hover:underline bg-indigo-50 border border-indigo-100 rounded transition-colors shrink-0"
                          title={t?.view_receipt || "Bekijk betaalbewijs"}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          <span>{t?.receipt || 'Betaalbewijs'}</span>
                        </a>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-gray-500 italic text-sm">{t.order_information_unavailable || 'Order information unavailable'}</div>
          )}
        </div>

        {orderData && orderData.cartItems && (
          <div className="mb-8">
            <h4 className="font-semibold text-gray-700 mb-4 uppercase text-xs tracking-wider flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63-.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              {t.cart_items || 'Cart Items'}
            </h4>
            <div className="overflow-x-auto rounded-lg border border-gray-200 mb-4 bg-white">
              <table className="w-full text-sm text-left">
                <thead className="bg-purple-100 text-primary text-xs uppercase">
                  <tr>
                    <th className="px-4 py-2 border-b border-purple-200">{t?.product || 'Product'}</th>
                    <th className="px-4 py-2 border-b border-purple-200 text-center">{t?.afas_artikelcode || t?.afasArticleCode || 'AFAS Article Code'}</th>
                    <th className="px-4 py-2 border-b border-purple-200 text-center">{t?.quantity || 'Quantity'}</th>
                    <th className="px-4 py-2 border-b border-purple-200 text-right">{t?.unit_price || 'Unit Price'}</th>
                    <th className="px-4 py-2 border-b border-purple-200 text-right">{t?.total_price || 'Total Price'}</th>
                  </tr>
                </thead>
                <tbody>
                  {orderData.cartItems.map((item, idxx) => {
                    const subtitle = item.item_subtitle || item.subtitle || (item.description && item.description !== (item.name || item.skuName) ? item.description : (item.project_name || item.project_id || null));
                    return (
                      <tr key={idxx} className="border-b last:border-b-0 hover:bg-gray-50">
                        <td className="px-4 py-2 text-gray-800">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-gray-900">{item.name || item.skuName || (t?.unknown_product || "Unknown Product")}</span>
                            {subtitle && (
                              <span className="text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded border border-gray-200">
                                {subtitle}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2 text-gray-800 text-center">
                        {item.afasArticleCode || item.afas_artikelcode ? (
                          <span
                            onClick={openAfasPiP}
                            className="cursor-pointer hover:bg-fuchsia-100 transition-colors text-xs font-mono text-primary bg-fuchsia-50 px-1.5 py-0.5 rounded border border-primary whitespace-nowrap"
                          >
                            {item.afasArticleCode || item.afas_artikelcode}
                          </span>
                        ) : (
                          <span className="inline-flex items-center justify-center bg-red-100 text-red-800 border border-red-200 rounded-full h-5 w-5 font-extrabold text-xs cursor-help select-none shrink-0" title="Missing AFAS Article Code">!</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-center text-gray-600">{item.quantity || 1}</td>
                      <td className="px-4 py-2 text-right text-gray-800">€ {parseFloat(item.price || 0).toFixed(2)}</td>
                      <td className="px-4 py-2 text-right text-gray-800 font-medium">€ {(parseFloat(item.price || 0) * (item.quantity || 1)).toFixed(2)}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {lines.length > 0 && (
          <div className="bg-gray-50/50">
            <SortableLinesTable
              lines={journal1Lines}
              title={t?.journal_1 || 'Journal 1 (Verkopen)'}
              language={language}
              t={t}
              icon={(
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
            />

            {/* Journal 2 Section with Toggle */}
            <div className="mb-6 last:mb-0">
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-semibold text-gray-700 uppercase text-[10px] tracking-widest flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2-2v12a2 2 0 002 2z" />
                  </svg>
                  {t?.journal_2 || 'Journal 2 (Transitorische posten)'}
                </h4>
                <div className="flex bg-gray-100 p-0.5 rounded-md border border-gray-200 shadow-inner">
                  <button
                    onClick={() => setJournal2ViewMode('grid')}
                    className={`px-3 py-1 text-[9px] font-bold rounded flex items-center transition-all ${(journal2ViewMode !== 'table') ? 'bg-white text-primary shadow-sm scale-105' : 'text-gray-400 hover:text-gray-600'}`}
                  >
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
                    {t?.grid || 'GRID'}
                  </button>
                  <button
                    onClick={() => setJournal2ViewMode('table')}
                    className={`px-3 py-1 text-[9px] font-bold rounded flex items-center transition-all ${journal2ViewMode === 'table' ? 'bg-white text-primary shadow-sm scale-105' : 'text-gray-400 hover:text-gray-600'}`}
                  >
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    {t?.table || 'TABLE'}
                  </button>
                </div>
              </div>

              {journal2ViewMode !== 'table' ? (
                <Journal2GridView lines={journal2Lines} language={language} t={t} />
              ) : (
                <SortableLinesTable
                  lines={journal2Lines}
                  title=""
                  language={language}
                  t={t}
                />
              )}
            </div>

            {otherLines.length > 0 && (
              <SortableLinesTable
                lines={otherLines}
                title={t?.other_afas_lines || 'Other AFAS Lines'}
                language={language}
                t={t}
                icon={(
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                )}
              />
            )}
          </div>
        )}
      </div>

      {showAfasPiP && (
        <AfasArticlePiP
          articles={afasArticles}
          onClose={() => setShowAfasPiP(false)}
        />
      )}
    </div>
  );
};

export default AdminAfasTransactionInnerDetails;
